import { useState, useEffect } from "react";
import axios from "axios";
import { Clock, Calendar, AlertCircle, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCountdown = (msRemaining) => {
  if (msRemaining <= 0) return "0h 0m";
  const totalMinutes = Math.floor(msRemaining / 60000);
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  return `${hours}h ${minutes}m`;
};

const formatTotalHours = (msRemaining) => {
  if (msRemaining <= 0) return 0;
  return Math.floor(msRemaining / 3600000);
};

export const OrderWindowBanner = () => {
  const [scheduleInfo, setScheduleInfo] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const fetchScheduleInfo = async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/schedule/info`);
        setScheduleInfo(data);
      } catch (error) {
        console.error("Failed to load schedule info:", error);
      }
    };
    fetchScheduleInfo();
    // Refresh schedule every 5 minutes (in case window opens/closes)
    const refreshId = setInterval(fetchScheduleInfo, 5 * 60 * 1000);
    return () => clearInterval(refreshId);
  }, []);

  useEffect(() => {
    // Tick every second for live countdown
    const tickId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(tickId);
  }, []);

  if (!scheduleInfo) return null;

  const cutoffTime = new Date(scheduleInfo.cutoff).getTime();
  const msRemaining = cutoffTime - now;
  const isOpen = scheduleInfo.is_open && msRemaining > 0;
  const totalHours = formatTotalHours(msRemaining);
  const countdownLabel = formatCountdown(msRemaining);

  // Use server-provided Central Time formatted strings to keep display
  // consistent regardless of browser timezone.
  const deliveryDateStr = scheduleInfo.delivery_display ||
    new Date(scheduleInfo.delivery_date).toLocaleDateString('en-US', {
      weekday: 'long', month: 'short', day: 'numeric'
    });
  const cutoffDateStr = scheduleInfo.cutoff_display ||
    new Date(scheduleInfo.cutoff).toLocaleString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit'
    });

  return (
    <div
      data-testid="order-window-banner"
      className={`${
        isOpen
          ? 'bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-300'
          : 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-300'
      } border-2 rounded-xl shadow-sm p-4 sm:p-5 mb-6`}
    >
      <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
        {/* Status + Countdown */}
        <div className="flex items-start gap-3 flex-1">
          {isOpen ? (
            <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0 mt-0.5" data-testid="banner-status-icon-open" />
          ) : (
            <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" data-testid="banner-status-icon-closed" />
          )}
          <div className="flex-1">
            <p
              data-testid="banner-status-label"
              className={`text-base sm:text-lg font-bold ${isOpen ? 'text-emerald-900' : 'text-amber-900'}`}
            >
              {isOpen ? 'Ordering Window Open' : 'Ordering Window Closed'}
            </p>
            {isOpen ? (
              <p className="text-sm text-gray-700 mt-0.5">
                Closes in{' '}
                <span data-testid="banner-countdown" className="font-bold text-emerald-700">
                  {countdownLabel}
                </span>{' '}
                <span className="text-gray-500">({totalHours} hours)</span>
              </p>
            ) : (
              <p className="text-sm text-gray-700 mt-0.5">
                {scheduleInfo.message || 'Window reopens Saturday at 12:00 AM.'}
              </p>
            )}
          </div>
        </div>

        {/* Cutoff + Delivery */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 lg:border-l lg:border-gray-300 lg:pl-6 w-full lg:w-auto">
          <div className="flex items-center gap-2">
            <Clock className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Cutoff</p>
              <p data-testid="banner-cutoff" className="text-sm text-gray-800 font-medium">
                {cutoffDateStr}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Calendar className="h-4 w-4 text-emerald-600 flex-shrink-0" />
            <div>
              <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Delivery</p>
              <p data-testid="banner-delivery-date" className="text-sm text-emerald-800 font-medium">
                {deliveryDateStr}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
